// web/src/components/audio_queue.ts — <audio> + Promise that resolves on `ended`.
//
// Pitfall #9 mitigation: callers should `await playAudio(url)` before
// firing the next-action transition, so praise + visual flourish do
// not race against the next-card mount.

export function playAudio(
  url: string,
  opts: { signal?: AbortSignal; onPlaying?: () => void } = {},
): Promise<void> {
  const { signal, onPlaying } = opts;
  return new Promise<void>((resolve, reject) => {
    const a = new Audio();
    let settled = false;
    a.src = url;
    a.preload = "auto";

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
    };

    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const rejectOnce = (reason: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(reason);
    };

    const onAbort = () => {
      a.pause();
      a.removeAttribute("src");
      a.load();
      rejectOnce(new DOMException("Audio playback aborted", "AbortError"));
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });
    if (onPlaying) a.addEventListener("playing", onPlaying, { once: true });
    a.addEventListener("ended", resolveOnce, { once: true });
    a.addEventListener("error", () => rejectOnce(new Error(`audio.error: ${url}`)), { once: true });
    a.play().catch(rejectOnce);
  });
}
